import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';
import { getAllBlogs, initializeBlogSystem, forceResetBlogPosts } from '../database/blogStore';
import BlogImage from '../components/BlogImage';
import { Clock, User, Tag, Calendar, Search, Filter } from 'lucide-react';

// Format the date in a more readable format
const formatDate = (dateString) => {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
};

// Get estimated reading time
const getReadingTime = (content) => {
  const wordsPerMinute = 200;
  const wordCount = content.trim().split(/\s+/).length;
  let baseReadingTime = Math.ceil(wordCount / wordsPerMinute);

  // Ensure reading time is between 1-3 minutes
  if (baseReadingTime < 1) baseReadingTime = 1;
  if (baseReadingTime > 3) baseReadingTime = 3;

  return baseReadingTime;
};

// Get excerpt from content
const getExcerpt = (content, maxLength = 200) => {
  if (!content) return '';

  // Try to get executive summary first
  const summaryMatch = content.match(/## Executive Summary\n\n([^#]+)/);
  if (summaryMatch) {
    const summary = summaryMatch[1].trim();
    return summary.length <= maxLength ? summary : summary.substring(0, maxLength) + '...';
  }

  // Remove markdown headers
  const withoutHeaders = content.replace(/^#+ .*$/gm, '').trim();

  // Get first paragraph
  const firstParagraph = withoutHeaders.split('\n\n')[0];

  if (firstParagraph.length <= maxLength) return firstParagraph;

  return firstParagraph.substring(0, maxLength) + '...';
};

const Blog = () => {
  const [posts, setPosts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedTag, setSelectedTag] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const { theme } = useTheme();

  // Load posts
  useEffect(() => {
    const loadPosts = async () => {
      try {
        setIsLoading(true);
        await forceResetBlogPosts();
        await initializeBlogSystem();
        const allPosts = await getAllBlogs('published');
        setPosts(allPosts);
      } catch (error) {
        console.error('Error loading blog posts:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadPosts();
  }, []);

  // Get all unique tags
  const tags = useMemo(() => {
    const tagSet = new Set();
    posts.forEach(post => {
      if (post.tags) {
        post.tags.forEach(tag => tagSet.add(tag));
      }
    });
    return Array.from(tagSet).sort();
  }, [posts]);

  // Filter posts by tag and search query
  const filteredPosts = useMemo(() => {
    // First sort by publishedDate
    const sortedPosts = [...posts].sort((a, b) => {
      const dateA = new Date(a.publishedDate);
      const dateB = new Date(b.publishedDate);
      return dateB - dateA;
    });

    // Then filter by tag and search
    return sortedPosts.filter(post => {
      const matchesTag = selectedTag === 'all' || (post.tags && post.tags.includes(selectedTag));
      const matchesSearch = !searchQuery ||
        post.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        post.content.toLowerCase().includes(searchQuery.toLowerCase());
      return matchesTag && matchesSearch;
    });
  }, [posts, selectedTag, searchQuery]);

  // Get featured post (most recent)
  const featuredPost = useMemo(() => {
    return filteredPosts[0];
  }, [filteredPosts]);

  if (isLoading) {
    return (
      <div className={`min-h-screen flex items-center justify-center ${theme === 'dark' ? 'bg-gray-900' : 'bg-white'}`}>
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className={`max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 ${theme === 'dark' ? 'bg-gray-900' : 'bg-slate-50'}`}>
      <div className="text-center mb-12">
        <h1 className={`text-5xl font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'} mb-4`}>
          Shared Oxygen Insights
        </h1>
        <p className={`text-xl ${theme === 'dark' ? 'text-gray-300' : 'text-slate-700'} max-w-2xl mx-auto`}>
          Executive perspectives on AI strategy, data architecture, and accountable implementation.
        </p>
      </div>

      {/* Search and Filter */}
      <div className="mb-12">
        <div className="flex flex-col sm:flex-row gap-4 items-center justify-between max-w-4xl mx-auto">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              placeholder="Search blog posts..."
              className={`w-full pl-10 pr-4 py-3 border rounded-lg focus:outline-none ${theme === 'dark'
                  ? 'bg-gray-800 border-gray-700 text-white placeholder-gray-400 focus:ring-blue-500'
                  : 'bg-white border-slate-300 text-slate-900 focus:ring-primary-500'
                } focus:ring-2 focus:border-transparent`}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
          <div className="relative">
            <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <select
              className={`pl-10 pr-8 py-3 border rounded-lg focus:outline-none focus:ring-2 appearance-none ${theme === 'dark'
                  ? 'bg-gray-800 border-gray-700 text-white focus:ring-blue-500'
                  : 'bg-white border-slate-300 text-slate-900 focus:ring-primary-500'
                }`}
              value={selectedTag}
              onChange={(e) => setSelectedTag(e.target.value)}
            >
              <option value="all">All Topics</option>
              {tags.map(tag => (
                <option key={tag} value={tag}>{tag}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Featured Post */}
      {featuredPost && (
        <div className="mb-16">
          <div className={`rounded-xl shadow-xl overflow-hidden ${theme === 'dark' ? 'bg-gray-800' : 'bg-white border border-slate-200'
            }`}>
            <div className="md:flex">
              <div className="md:w-1/2 relative">
                <BlogImage
                  title={featuredPost.title}
                  imageUrl={featuredPost.imageUrl}
                  size="large"
                  className="h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-r from-black/50 via-black/25 to-transparent md:bg-gradient-to-t md:from-black/50 md:via-transparent md:to-transparent"></div>
              </div>
              <div className={`md:w-1/2 p-8 flex flex-col justify-between ${theme === 'dark'
                  ? 'bg-gradient-to-b from-gray-800 to-gray-900'
                  : 'bg-gradient-to-b from-white to-slate-100/70'
                }`}>
                <div>
                  <div className="flex flex-wrap gap-2 mb-6">
                    {featuredPost.tags?.map((tag, index) => (
                      <span
                        key={index}
                        className={`px-3 py-1 rounded-full text-sm font-medium ${theme === 'dark'
                            ? 'bg-blue-900/40 text-blue-300'
                            : 'bg-blue-100 text-blue-800'
                          }`}
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                  <h2 className={`text-3xl font-bold mb-4 ${theme === 'dark' ? 'text-white' : 'text-gray-900'
                    }`}>
                    {featuredPost.title}
                  </h2>
                  <p className={`text-lg mb-6 line-clamp-3 ${theme === 'dark' ? 'text-gray-300' : 'text-slate-700'
                    }`}>
                    {featuredPost.excerpt || getExcerpt(featuredPost.content)}
                  </p>
                </div>
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                  <div className={`flex items-center space-x-4 text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-slate-600'
                    }`}>
                    <div className="flex items-center">
                      <User className="w-4 h-4 mr-2" />
                      <span className="font-medium">Admin</span>
                    </div>
                    <div className="flex items-center">
                      <Calendar className="w-4 h-4 mr-2" />
                      <span>{formatDate(featuredPost.publishedDate || featuredPost.createdAt)}</span>
                    </div>
                    <div className="flex items-center">
                      <Clock className="w-4 h-4 mr-2" />
                      <span>{getReadingTime(featuredPost.content)} min read</span>
                    </div>
                  </div>
            <Link
              to={`/blog/${featuredPost.slug || featuredPost.id}`}
                    className="inline-flex items-center justify-center px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
                  >
                    Read Article
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Blog Posts Grid */}
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {filteredPosts.slice(1).map(post => (
            <Link
              key={post.slug || post.id}
              to={`/blog/${post.slug || post.id}`}
            className={`rounded-lg shadow-lg overflow-hidden transform hover:-translate-y-1 transition-all duration-300 flex flex-col group ${theme === 'dark' ? 'bg-gray-800' : 'bg-white border border-slate-200'
              }`}
          >
            <div className="relative h-48">
              <BlogImage
                title={post.title}
                imageUrl={post.imageUrl}
                size="medium"
                className="h-full w-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/30 to-transparent opacity-60 group-hover:opacity-80 transition-opacity"></div>
              <div className="absolute bottom-0 left-0 right-0 p-4">
                <div className="flex flex-wrap gap-2 mb-2">
                  {post.tags?.map((tag, index) => (
                    <span
                      key={index}
                      className="px-2 py-1 bg-white/20 backdrop-blur-sm text-white rounded-full text-xs font-medium"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            </div>
            <div className="p-6 flex-1 flex flex-col">
              <h3 className={`text-xl font-bold mb-3 transition-colors ${theme === 'dark'
                  ? 'text-white group-hover:text-blue-400'
                  : 'text-gray-900 group-hover:text-blue-700'
                }`}>
                {post.title}
              </h3>
              <p className={`mb-4 flex-1 ${theme === 'dark' ? 'text-gray-300' : 'text-slate-700'
                }`}>
                {post.excerpt || getExcerpt(post.content)}
              </p>
              <div className="flex items-center justify-between text-sm">
                <div className={`flex items-center space-x-4 ${theme === 'dark' ? 'text-gray-400' : 'text-slate-600'
                  }`}>
                  <div className="flex items-center">
                    <Calendar className="w-4 h-4 mr-1" />
                    <span>{formatDate(post.publishedDate || post.createdAt)}</span>
                  </div>
                  <div className="flex items-center">
                    <Clock className="w-4 h-4 mr-1" />
                    <span>{getReadingTime(post.content)} min</span>
                  </div>
                </div>
                <span className={`font-medium ${theme === 'dark'
                    ? 'text-blue-400 group-hover:text-blue-300'
                    : 'text-blue-700 group-hover:text-blue-800'
                  }`}>
                  Read More →
                </span>
              </div>
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
};

export default Blog;
